import moment from 'moment';
import * as _ from 'lodash';
import { Nullable } from 'global';
import { Invoice } from 'modules/invoice';
import { CreateProposalDTO } from 'modules/proposals';
import { FinanceProgram } from 'modules/financeProgram';
import {
  SaleType,
  RateTypes,
  MarkupType,
  Structures,
  ProgramTypes,
  MarkupMethods,
  FinanceFormValues,
  PaymentCalculationMethods,
} from 'modules/desking/shared';
import {
  Markup,
  SubsidyCapType,
  ProposalSaleType,
  LenderMarketRateInput,
  SubmitProposalOptionActionType,
  CreditTiersResult,
} from '@trnsact/trnsact-shared-types';

import { getFinanceProgramTierValues } from '../../lib';
import { decisionCodeToStatus } from '../../constants';
import { LenderDeskingPanelFormValues } from '../types';
import {
  prepareRates,
  convertFeeFormValueToFee,
  convertProgramConfigToFormValues,
  convertInvoiceToSaleDetailsFormValues,
} from '../../../desking/lib';

class LenderDeskingPanelMapper {
  private initialFinanceValues: LenderDeskingPanelFormValues['financeQuote'] = {
    fee: [],
    terms: [],
    amount: 0,
    menu: null,
    program: null,
    commonRate: 0,
    residual: null,
    programName: '',
    dealerMarkup: 0,
    markupRate: null,
    specifyRates: [],
    advancePayment: '',
    specifyRate: false,
    maxApprovalAmount: 0,
    specifyRatePerTerm: 0,
    daysToFirstPayment: '30',
    structure: Structures.None,
    selectedSubmissionId: null,
    configureSubsidyCap: false,
    specifyLenderMarketRates: [],
    subsidyType: MarkupType.Flat,
    minimumRequiredDownPayment: 0,
    programType: ProgramTypes.Loan,
    rateType: RateTypes.InterestRate,
    markupMethod: MarkupMethods.MarkupDollar,
    startDate: moment().format('YYYY-MM-DD'),
    paymentCalculationMethod: PaymentCalculationMethods.Arrears,
    firstPaymentDate: moment().add(1, 'month').format('YYYY-MM-DD'),

    // ???
    customTermTemporaryValue: null,
    createProposalWithExistingProgram: null,
    commonLenderMarketRate: '',
    subsidyCapConfiguration: {
      lenderName: '',
      maxSubsidyAmount: 0,
      netInvoiceAmount: 0,
      lenderMarketRates: [],
      netInvoicePercentage: 0,
      subsidyCapType: SubsidyCapType.FixedAmount,
    },
  };

  private initialEquipmentValues: LenderDeskingPanelFormValues['equipment'] = {
    year: '',
    make: '',
    model: '',
    class: '',
    stockId: '',
    newUsed: '',
    mileage: 0,
    quantity: '',
    description: '',
    retailPrice: 0,
    serialNumberVin: '',
    selectedEquipment: null,
    totalWholesaleCost: 0,
  };

  private mapEquipment(equipmentList: Array<any>): LenderDeskingPanelFormValues['equipment'] {
    if (!equipmentList.length) return this.initialEquipmentValues;

    const [equipment] = equipmentList;

    return {
      year: equipment.year ?? '',
      make: equipment.make ?? '',
      model: equipment.model ?? '',
      stockId: equipment.stockId ?? '',
      newUsed: equipment.newUsed ?? '',
      mileage: equipment.mileage ?? 0,
      class: equipment.classType ?? '',
      retailPrice: equipment.retailPrice ?? 0,
      quantity: equipment.quantity?.toString() ?? '',
      selectedEquipment: equipment.equipmentId ?? null,
      serialNumberVin: equipment.serialNumberVin ?? '',
      totalWholesaleCost: equipment.wholesaleCost ?? 0,
      description: equipment.additionalDescription ?? '',
    };
  }

  private mapPhone(customerData: any) {
    return (
      (customerData?.phone || customerData?.mobilePhone || customerData?.homePhone || customerData?.workPhone) ?? ''
    );
  }

  public processCustomer(creditAppData: Record<string, any>, customerData: any) {
    if (!creditAppData) return { name: '', phone: '', email: '', contact: '' };

    return {
      name: customerData?.title ?? '',
      email: customerData?.email ?? '',
      phone: this.mapPhone(customerData),
      contact: customerData?.ownerPgId ?? '',
    };
  }

  public processSaleDetails(invoice: Invoice) {
    return {
      saleType: SaleType.Financing,
      // TODO temporary solution to save time
      ...convertInvoiceToSaleDetailsFormValues(invoice),
    };
  }

  public initPanel(params: {
    cs: any;
    invoice: Invoice;
    equipment: Array<any>;
    preSelectedValue: any[];
  }): LenderDeskingPanelFormValues {
    const { invoice, cs, equipment, preSelectedValue } = params;

    return {
      actions: {
        sendVia: 'Email',
        notifyUsers: preSelectedValue,
      },
      lenderDescription: '',
      equipment: this.mapEquipment(equipment),
      submission: {
        noteToBuyer: cs?.notes ?? '',
        maxApprovalAmount: cs?.approvedAmount ?? 0,
        selectedSubmissionId: cs?.creditSubmissionId,
        decision: cs?.decision ? decisionCodeToStatus[cs.decision as keyof typeof decisionCodeToStatus].toString() : '',
      },
      displaySettings: {
        productPrice: false,
        lenderProfileId: null,
        showLenderName: false,
        showLenderLogo: false,
        productDetails: false,
        showBestOptions: false,
        showInterestRate: false,
        shouldRedirectToOCA: false,
        excludeEquipmentSales: false,
        showFinanceProgramDescription: true,
      },
      financeProgramDescription: {
        apiValue: '',
        editorValue: '',
      },
      financeQuote: {
        ...this.initialFinanceValues,
        amount: invoice.invoiceTotal,
        maxApprovalAmount: cs?.approvedAmount ?? 0,
      },
    };
  }

  public processFormToCreateDto(params: {
    invoice: Invoice;
    isDraft: boolean;
    customerData: any;
    creditAppData: any;
    financePricing: Record<string, any>;
    formValues: LenderDeskingPanelFormValues;
    ccEmails?: Partial<Record<'lender' | 'dealer', string>>;
  }): Omit<CreateProposalDTO, 'vendorOpportunityId' | 'creditSubmissionId' | 'noteToBuyer'> {
    const { isDraft, formValues, financePricing, ccEmails, invoice, creditAppData, customerData } = params;

    const saleDetails = this.processSaleDetails(invoice);
    const customer = this.processCustomer(creditAppData, customerData);

    let dealerMarkup: Nullable<Markup> = null;

    if (![Structures.None, Structures.Subsidy].includes(formValues.financeQuote.structure)) {
      const { markupMethod, markupRate } = formValues.financeQuote;

      const mapping: Record<MarkupMethods, Record<string, any>> = {
        [MarkupMethods.MarkupPercentage]: { markupPercentage: markupRate ?? 0 },
        [MarkupMethods.MarkupDollar]: { markupFlat: markupRate ?? 0 },
        [MarkupMethods.CustomerRate]: {},
      };

      if (markupMethod) {
        dealerMarkup = {
          identifier: 'Dealer Markup',
          ...mapping[markupMethod],
        };
      }
    }

    let lenderMarketRatesToSend: LenderMarketRateInput[] = [];

    if (
      formValues.financeQuote.subsidyCapConfiguration?.lenderMarketRates &&
      formValues.financeQuote.subsidyCapConfiguration?.lenderMarketRates?.length > 0
    ) {
      try {
        const termsToUse = _.orderBy(formValues.financeQuote.terms.map(Number));

        if (formValues.financeQuote.specifyRate && formValues.financeQuote?.specifyLenderMarketRates?.length > 0) {
          lenderMarketRatesToSend = formValues.financeQuote.specifyLenderMarketRates?.map((rate, index) => ({
            term: termsToUse[index] ?? 0,
            rate: Number(
              formValues.financeQuote.specifyRate
                ? (formValues.financeQuote.specifyLenderMarketRates[index]?.rate ?? 0)
                : formValues.financeQuote.commonLenderMarketRate
            ),
          }));
        } else {
          lenderMarketRatesToSend = formValues.financeQuote.subsidyCapConfiguration?.lenderMarketRates?.map(
            (rate, index) => ({
              term: _.orderBy(termsToUse, 'term', 'asc')[index] ?? 0,
              rate: Number(formValues.financeQuote.commonLenderMarketRate),
            })
          );
        }
      } catch (e: any) {
        console.error('collectFinanceVariablesForCalc: Error converting lender market rates', e.message);
        lenderMarketRatesToSend = [];
      }
    }

    return {
      sendProposalData: !isDraft
        ? {
            sendViaEmail: true,
            sendViaText: false,
            ccEmail: Object.values(ccEmails ?? {}),
            phoneNumber: customer.phone ?? null,
          }
        : null,
      saleType: ProposalSaleType.FinanceOnly,
      financeProgramId: formValues.financeQuote.program?.value ?? null,
      settings: {
        behavior: {
          submitOptionActionType: SubmitProposalOptionActionType.AcceptOption,
        },
        configuration: {
          builder: '',
          productsConfig: '',
          formData: {
            ...formValues,
            submission: {
              selectedSubmissionId: formValues.submission.selectedSubmissionId,
              maxApprovalAmount: formValues.financeQuote.maxApprovalAmount ?? 0,
            },
            financeQuote: {
              ...formValues.financeQuote,
              program: (formValues.financeQuote.program?.value ?? null) as any, // hotfix to avoid discrepancies with dealer proposal form data,
            },
          },
          pricing: financePricing,
          commonProductsConfiguration: '',
        },
        meta: {
          equipmentId: formValues.equipment.selectedEquipment,
        },
        view: {
          isShowProductDetails: false,
          isShowProductPrice: false,
          isShowLenderLogo: false,
          isShowLenderName: false,
          isShowInterestRate: false,
          isShowBestOptions: false,
          excludeEquipmentSales: false,
          isShowFinanceProgramDescription: true,
        },
      },
      paymentOptions: {
        finance: {
          menu: null,
          markups: [],
          dealerMarkup,
          salesTaxPercentage: 0,
          saleType: ProposalSaleType.FinanceOnly,
          rateType: formValues.financeQuote.rateType,
          amountFinanced: formValues.financeQuote.amount,
          downPaymentAmount: saleDetails.downPayment.flat,
          residualPercentage: formValues.financeQuote.residual ?? 0,
          financeProgramName: formValues.financeQuote.programName || '',
          maxApprovalAmount: formValues.financeQuote.maxApprovalAmount ?? 0,
          paymentPeriodTiming: formValues.financeQuote.paymentCalculationMethod,
          financeProgramType: formValues.financeQuote.programType.toUpperCase(),
          daysToFirstPayment: Number(formValues.financeQuote.daysToFirstPayment),
          isSubsidized: formValues.financeQuote.structure === Structures.Subsidy,
          numberOfAdvancePayments: Number(formValues.financeQuote.advancePayment || 0),
          startDate: new Date(formValues.financeQuote.startDate).toISOString().split('T')[0],
          terms: prepareRates({
            terms: formValues.financeQuote.terms,
            commonRate: formValues.financeQuote.commonRate,
            specifyRate: formValues.financeQuote.specifyRate,
            specifyRates: formValues.financeQuote.specifyRates,
          }),
          flatFees: formValues.financeQuote.fee
            .filter(({ type }) => type === MarkupType.Flat)
            .map(convertFeeFormValueToFee),
          percentageFees: formValues.financeQuote.fee
            .filter(({ type }) => type === MarkupType.Percentage)
            .map(convertFeeFormValueToFee),
          customerRate:
            (formValues.financeQuote.structure !== Structures.None &&
              formValues.financeQuote.markupMethod === MarkupMethods.CustomerRate) ||
            formValues.financeQuote.structure === Structures.Subsidy
              ? formValues.financeQuote.markupRate
              : null,
          subsidyCapConfiguration:
            formValues.financeQuote.structure === Structures.Subsidy && formValues.financeQuote.configureSubsidyCap
              ? { ...formValues.financeQuote.subsidyCapConfiguration, lenderMarketRates: lenderMarketRatesToSend }
              : null,
        },
      },
    };
  }

  public processProgramToForm(program: Nullable<FinanceProgram>, scoreCardResult?: CreditTiersResult) {
    if (!program) return this.initialFinanceValues;

    // TODO temporary solution to save time
    let formValues = convertProgramConfigToFormValues(program.paymentOptionsConfiguration) as any as Partial<
      FinanceFormValues['financeQuote']
    >;

    const isNeedToApplyFPTiers =
      program?.paymentOptionsConfiguration?.isTiersEnabled &&
      !!program?.scoreTiers?.tiers?.length &&
      !!scoreCardResult?.highestTierPassed;

    if (isNeedToApplyFPTiers) {
      formValues = {
        ...formValues,
        ...getFinanceProgramTierValues(program.scoreTiers!.tiers!, scoreCardResult),
      };
    }

    return formValues;
  }

  public processNotifyLabel(contact: any, includeEmail = false) {
    const label = `${contact.fullName}${contact.title ? `, ${contact.title}` : ``}`;
    return includeEmail ? `${label} - ${contact.email}` : label;
  }
}

export const lenderDeskingPanelMapper = new LenderDeskingPanelMapper();
